import { Directive, ElementRef, OnInit, Renderer2, Optional, OnDestroy } from '@angular/core';
import { NgControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Directive({
  selector: '[appRequiredField]',
  standalone: true
})
export class RequiredFieldDirective implements OnInit, OnDestroy {
  // Subject para gerenciar a desinscrição dos observables e evitar memory leaks
  private destroy$ = new Subject<void>();

  constructor(
    private el: ElementRef,
    @Optional() private ngControl: NgControl,
    private renderer: Renderer2
  ) {}

  ngOnInit() {
    if (!this.ngControl?.control) {
      console.error('RequiredFieldDirective: NgControl não encontrado para o elemento.', this.el.nativeElement);
      return;
    }

    const control = this.ngControl.control;

    // Ouve as mudanças de status (valid/invalid) ou valor do controle.
    // O takeUntil gerencia a desinscrição automaticamente quando o componente é destruído.
    control.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.updateErrorMessage();
    });

    // É crucial ouvir o evento 'blur', pois é quando o Angular marca o controle como 'touched'.
    // A mensagem de erro deve aparecer após o usuário sair do campo.
    this.renderer.listen(this.el.nativeElement, 'blur', () => {
      this.updateErrorMessage();
    });

    // Uma chamada inicial pode ser útil em alguns cenários, mas geralmente não é necessária.
    // this.updateErrorMessage();
  }

  private updateErrorMessage() {
    const control = this.ngControl?.control;
    if (!control) return;

    const matFormField = this.el.nativeElement.closest('mat-form-field');
    if (!matFormField) return;

    // Encontra o container onde o Angular Material coloca as mensagens de erro/dica.
    const subscriptWrapper = matFormField.querySelector('.mat-mdc-form-field-subscript-wrapper');
    if (!subscriptWrapper) return;

    // 1. Limpa qualquer mensagem de erro que a diretiva tenha adicionado anteriormente.
    const oldError = subscriptWrapper.querySelector('.app-required-field-error');
    if (oldError) {
      this.renderer.removeChild(subscriptWrapper, oldError);
    }

    // 2. Condição para mostrar o erro: o campo deve ser inválido E o usuário deve ter interagido com ele.
    if (control.invalid && (control.touched || control.dirty)) {
      const errorElement = this.renderer.createElement('mat-error');
      let errorMessage = 'Campo inválido'; // Mensagem padrão

      // Sua lógica para determinar a mensagem de erro (está perfeita!)
      if (control.errors?.['required']) {
        errorMessage = 'Campo Obrigatório';
      } else if (control.errors?.['cpfInvalid']) {
        errorMessage = 'CPF Inválido';
      } else if (control.errors?.['email']) {
        errorMessage = 'Email inválido';
      } else if (control.errors?.['minlength']) {
        errorMessage = `Mínimo de ${control.errors['minlength'].requiredLength} caracteres`;
      } else if (control.errors?.['cepInvalid']) {
        errorMessage = 'O CEP precisa conter 8 digitos e não pode conter letras, pontos e caracteres especiais.';
      } // ... e assim por diante para outros erros.

      const text = this.renderer.createText(errorMessage);
      this.renderer.appendChild(errorElement, text);

      // Adiciona classes para estilização e identificação
      this.renderer.addClass(errorElement, 'mat-error');
      this.renderer.addClass(errorElement, 'app-required-field-error'); // Classe própria para fácil remoção
      this.renderer.setAttribute(errorElement, 'role', 'alert');

      this.renderer.appendChild(subscriptWrapper, errorElement);
    }
  }

  ngOnDestroy() {
    // Dispara o subject para desinscrever de todos os observables
    this.destroy$.next();
    this.destroy$.complete();
  }
}
